import ErrorBoundary from '@/components/ErrorBoundary'
import CustomerSelect from '@/components/Form/Shared/CustomerSelect'
import DepartmentSelect from '@/components/Form/Shared/DepartmentSelect'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import TicketSelect from '@/components/Form/Shared/TicketSelect'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import useAutoCall from '@/hooks/useAutoCall'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import AddIcons from '@/icons/AddIcons'
import CloseIcons from '@/icons/CloseIcons'
import useAuthStore from '@/stores/useAuthStore'
import { formatDateWithFormatter, formatPGDate } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { AnimatePresence, motion } from 'framer-motion'
import React from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
    Button,
    GridHookForm,
    RadioGroup,
    TextField,
    Typogaphy,
} from 'vnetwork-ui'
import { PARTICIPANTS_OPTIONS, ROOM_OPTIONS } from '../constants'
import ParticipantDisplay from '../ParticipantDisplay'
import { createEvent } from '../services'
import AvailableTimePicker from '../shared/AvailableTimePicker'
import { EventFormData } from '../types'
import validationSchema from './yub'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface CreateEventFormProps {
    onClose: () => void
    initialDate?: Date
}

const CreateEventForm: React.FC<CreateEventFormProps> = ({
    onClose,
    initialDate,
}) => {
    const { employee } = useAuthStore()
    const { t } = useTranslation()

    const { refetch } = useQueryClientCallback({
        pattern: 'events',
        mode: 'contains',
    })

    const formControl = useForm<EventFormData>({
        mode: 'onChange',
        defaultValues: {
            title: '',
            event_type: 'meeting',
            start_date: initialDate
                ? formatDateWithFormatter(dayjs(initialDate), 'YYYY-MM-DD')
                : formatDateWithFormatter(dayjs(new Date()), 'YYYY-MM-DD'),
            start_time: '09:00',
            duration_minutes: 30,
            location: '',
            description: '',
            room_enabled: true,
            room_name: 'small_meeting_room',
            participant_scope: 'team',
            departments: [],
            employees: employee ? [employee.employee_id] : [],
            ticket_related: [],
            customer_related: [],
        },
        // @ts-ignore
        resolver: yupResolver(validationSchema),
    })

    // Use useWatch for better performance
    const [
        startDate,
        startTime,
        durationMinutes,
        roomEnabled,
        participantScope,
        roomName,
        location,
        description,
    ] = useWatch({
        control: formControl.control,
        name: [
            'start_date',
            'start_time',
            'duration_minutes',
            'room_enabled',
            'participant_scope',
            'room_name',
            'location',
            'description',
        ],
    })

    // Create event mutation
    const {
        mutateAsync: createEventMutation,
        isPending,
        isSuccess,
    } = useMutationWithHandler(
        {
            mutationFn: createEvent,
            onSuccess: () => {
                refetch()
            },
        },
        {
            reactQueryKeys: QUERY_KEYS.EVENTS,
            errorMessage: 'Create Event Failed',
        }
    )

    // Auto close after 5 seconds when success
    useAutoCall({
        func: () => {
            onClose()
            refetch()
        },
        ms: 5000,
        enabled: isSuccess,
    })

    // Calculate end time based on start time and duration
    const calculateEndTime = (
        startTime: string,
        durationHours: number,
        durationMinutes: number
    ) => {
        const [hours, minutes] = startTime.split(':').map(Number)
        const totalMinutes =
            hours * 60 + minutes + durationHours * 60 + durationMinutes
        const endHours = Math.floor(totalMinutes / 60) % 24
        const endMinutes = totalMinutes % 60
        return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`
    }

    // Format date for display
    const formatDateForDisplay = (dateStr: string) => {
        try {
            const date = new Date(dateStr)
            return date.toLocaleDateString('vi-VN', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            })
        } catch {
            return dateStr
        }
    }

    const handleSubmit = async (values: EventFormData) => {
        console.log('🔍 handleSubmit received values:', values)
        console.log('🔍 start_date before format:', values.start_date)

        const formattedStartDate = formatPGDate(
            values.start_date,
            'YYYY-MM-DDTHH:mm:ssZ'
        )
        console.log('🔍 start_date after format:', formattedStartDate)

        const payload = {
            ...values,
            start_date: formattedStartDate as string,
        }
        console.log('🔍 Final payload before API call:', payload)

        await createEventMutation(payload)
    }

    return (
        <ErrorBoundary>
            <div className="h-full flex flex-col">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 relative">
                    <img
                        src="/images/create-event.png"
                        alt="Header pattern"
                        className="w-full h-full object-cover"
                    />
                </div>
                {isSuccess && (
                    <ParticipantDisplay
                        start_date={startDate}
                        start_time={startTime}
                        duration_minutes={durationMinutes}
                        room_enabled={roomEnabled}
                        participant_scope={participantScope}
                        room_name={roomName}
                        location={location}
                        description={description}
                    />
                )}
                {!isSuccess && (
                    <div className="flex-1 mt-4 overflow-auto">
                        <div className="rounded-lg p-4">
                            <Typogaphy className="text-lg font-semibold text-gray-900 mb-4 text-center">
                                {t('dashboard.events.create_event')}
                            </Typogaphy>
                            <GridHookForm
                                className="grid !grid-cols-3"
                                formControl={formControl}
                                onSubmit={handleSubmit}
                                data={[
                                    {
                                        formItemProps: {
                                            label: t('dashboard.events.title'),
                                            name: 'title',
                                            type: 'textfield',
                                            placeholder:
                                                'Mar & Sale | Họp gặp xử lí lỗi file',
                                        },
                                        className: 'col-span-4',
                                    },
                                    {
                                        formItemProps: {
                                            label: t(
                                                'dashboard.events.start_date'
                                            ),
                                            name: 'start_date',
                                            type: 'datepicker',
                                            placeholder: 'Chọn ngày',
                                        },
                                        className: 'col-span-1',
                                    },
                                    {
                                        className: 'col-span-3',
                                        renderEditComponent: () => {
                                            return (
                                                <AvailableTimePicker
                                                    setStartTimeError={(
                                                        error
                                                    ) => {
                                                        formControl.setError(
                                                            'start_time',
                                                            {
                                                                message: error,
                                                            }
                                                        )
                                                    }}
                                                    selectedRoom={
                                                        roomEnabled
                                                            ? formControl.watch(
                                                                  'room_name'
                                                              )
                                                            : undefined
                                                    }
                                                    selectedDate={startDate}
                                                    startTime={startTime}
                                                    duration={durationMinutes}
                                                    onStartTimeChange={(time) =>
                                                        formControl.setValue(
                                                            'start_time',
                                                            time
                                                        )
                                                    }
                                                    onDurationChange={(
                                                        duration
                                                    ) =>
                                                        formControl.setValue(
                                                            'duration_minutes',
                                                            duration
                                                        )
                                                    }
                                                />
                                            )
                                        },
                                    },
                                    {
                                        className: 'col-span-4',
                                        renderEditComponent: () => {
                                            if (!startDate || !startTime)
                                                return null

                                            const endTime = calculateEndTime(
                                                startTime,
                                                0,
                                                durationMinutes || 0
                                            )
                                            const displayDate =
                                                formatDateForDisplay(startDate)

                                            return (
                                                <div className="col-span-1">
                                                    <Typogaphy className="text-sm text-gray-600">
                                                        {t(
                                                            'dashboard.events.event_time',
                                                            {
                                                                date: displayDate,
                                                                startTime:
                                                                    startTime,
                                                                endTime:
                                                                    endTime,
                                                            }
                                                        )}
                                                    </Typogaphy>
                                                </div>
                                            )
                                        },
                                    },
                                    {
                                        renderEditComponent: () => {
                                            return (
                                                <motion.div
                                                    className="relative"
                                                    animate={{
                                                        height: roomEnabled
                                                            ? 100
                                                            : 'auto',
                                                    }}
                                                    transition={{
                                                        duration: 0.3,
                                                        ease: 'easeInOut',
                                                    }}
                                                >
                                                    <motion.div
                                                        animate={{
                                                            opacity: roomEnabled
                                                                ? 0.7
                                                                : 1,
                                                        }}
                                                        transition={{
                                                            duration: 0.2,
                                                        }}
                                                    >
                                                        <Controller
                                                            name="location"
                                                            control={
                                                                formControl.control
                                                            }
                                                            render={({
                                                                field,
                                                            }) => (
                                                                <TextField
                                                                    size="small"
                                                                    className={
                                                                        roomEnabled
                                                                            ? 'h-[100px]'
                                                                            : ''
                                                                    }
                                                                    label={t(
                                                                        'dashboard.events.location'
                                                                    )}
                                                                    value={
                                                                        roomEnabled
                                                                            ? ' '
                                                                            : field.value
                                                                    }
                                                                    readOnly={
                                                                        roomEnabled
                                                                    }
                                                                    onChange={
                                                                        field.onChange
                                                                    }
                                                                    placeholder={t(
                                                                        'dashboard.events.meeting_link'
                                                                    )}
                                                                />
                                                            )}
                                                        />
                                                    </motion.div>

                                                    <motion.div
                                                        animate={{
                                                            width: roomEnabled
                                                                ? 'calc(100% - 32px)'
                                                                : 'auto',
                                                            top: roomEnabled
                                                                ? '8px'
                                                                : '50%',
                                                            transform:
                                                                roomEnabled
                                                                    ? 'translateY(0)'
                                                                    : 'translateY(-50%)',
                                                        }}
                                                        transition={{
                                                            duration: 0.3,
                                                            ease: 'easeInOut',
                                                        }}
                                                        className="absolute right-4"
                                                    >
                                                        <Button
                                                            size="small"
                                                            type="button"
                                                            startIcon={
                                                                !roomEnabled ? (
                                                                    <AddIcons />
                                                                ) : (
                                                                    <CloseIcons />
                                                                )
                                                            }
                                                            onClick={() => {
                                                                formControl.setValue(
                                                                    'room_enabled',
                                                                    !roomEnabled
                                                                )
                                                            }}
                                                            className={`bg-[#F3F8FF] h-[27px] text-primary ${!roomEnabled ? 'w-auto' : 'w-full'}`}
                                                        >
                                                            {t(
                                                                'dashboard.events.add_meeting_room'
                                                            )}
                                                        </Button>
                                                    </motion.div>

                                                    <AnimatePresence>
                                                        {roomEnabled && (
                                                            <motion.div
                                                                initial={{
                                                                    opacity: 0,
                                                                    y: -10,
                                                                }}
                                                                animate={{
                                                                    opacity: 1,
                                                                    y: 0,
                                                                }}
                                                                exit={{
                                                                    opacity: 0,
                                                                    y: -10,
                                                                }}
                                                                transition={{
                                                                    duration: 0.3,
                                                                    delay: 0.1,
                                                                }}
                                                                className="absolute bottom-0 left-4"
                                                            >
                                                                <Controller
                                                                    name="room_name"
                                                                    control={
                                                                        formControl.control
                                                                    }
                                                                    render={({
                                                                        field,
                                                                    }) => (
                                                                        <RadioGroup
                                                                            options={ROOM_OPTIONS(
                                                                                t
                                                                            )}
                                                                            direction="row"
                                                                            value={
                                                                                field.value
                                                                            }
                                                                            onChange={
                                                                                field.onChange
                                                                            }
                                                                        />
                                                                    )}
                                                                />
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </motion.div>
                                            )
                                        },
                                        className: 'col-span-4',
                                    },
                                    {
                                        className: 'col-span-4',
                                        renderEditComponent: () => {
                                            return (
                                                <div className="flex flex-col gap-2 px-4">
                                                    <Typogaphy className="text-sm text-gray-600">
                                                        {t(
                                                            'dashboard.events.participants'
                                                        )}
                                                        :
                                                    </Typogaphy>
                                                    <Controller
                                                        name="participant_scope"
                                                        control={
                                                            formControl.control
                                                        }
                                                        render={({ field }) => (
                                                            <RadioGroup
                                                                options={PARTICIPANTS_OPTIONS(
                                                                    t
                                                                )}
                                                                direction="row"
                                                                value={
                                                                    field.value
                                                                }
                                                                onChange={
                                                                    field.onChange
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            )
                                        },
                                    },
                                    {
                                        className:
                                            participantScope === 'department'
                                                ? 'col-span-4'
                                                : 'hidden',
                                        renderEditComponent: () => {
                                            return (
                                                <DepartmentSelect
                                                    control={
                                                        formControl.control
                                                    }
                                                    name="departments"
                                                    multiple={true}
                                                />
                                            )
                                        },
                                    },
                                    {
                                        className:
                                            participantScope === 'team'
                                                ? 'col-span-4'
                                                : 'hidden',
                                        renderEditComponent: () => {
                                            return (
                                                <EmployeeSelect
                                                    label={t(
                                                        'dashboard.events.participants.employees'
                                                    )}
                                                    control={
                                                        formControl.control
                                                    }
                                                    name="employees"
                                                    multiple={true}
                                                />
                                            )
                                        },
                                    },
                                    {
                                        className: 'col-span-4',
                                        renderEditComponent: () => {
                                            return (
                                                <Controller
                                                    name="description"
                                                    control={
                                                        formControl.control
                                                    }
                                                    render={({ field }) => (
                                                        <TextEditorCustomize
                                                            label={t(
                                                                'dashboard.events.description'
                                                            )}
                                                            placeholder={t(
                                                                'dashboard.events.editor_placeholder'
                                                            )}
                                                            value={{
                                                                text: field.value,
                                                                attachments: [],
                                                            }}
                                                            onChange={(value) =>
                                                                field.onChange(
                                                                    value.text
                                                                )
                                                            }
                                                        />
                                                    )}
                                                />
                                            )
                                        },
                                    },
                                    {
                                        className: 'col-span-4',
                                        renderEditComponent: () => {
                                            return (
                                                <TicketSelect
                                                    control={
                                                        formControl.control
                                                    }
                                                    name="ticket_related"
                                                    multiple={true}
                                                />
                                            )
                                        },
                                    },
                                    {
                                        className: 'col-span-4',
                                        renderEditComponent: () => {
                                            return (
                                                <CustomerSelect
                                                    showLabel={true}
                                                    control={
                                                        formControl.control
                                                    }
                                                    name="customer_related"
                                                    multiple={true}
                                                />
                                            )
                                        },
                                    },
                                ]}
                                actions={[
                                    {
                                        type: 'button',
                                        variant: 'outlined',
                                        title: t('dashboard.events.cancel'),
                                        handler: onClose,
                                    },
                                    {
                                        type: 'submit',
                                        variant: 'contained',
                                        title: t(
                                            'dashboard.events.create_event'
                                        ),
                                        loading: isPending,
                                        disabled: isPending,
                                    },
                                ]}
                            />
                        </div>
                    </div>
                )}
            </div>
        </ErrorBoundary>
    )
}

export default CreateEventForm
